import { createContext, useCallback, useContext, useMemo, useRef } from "react";

import { twMerge } from "@/utils/twMerge";

import type { ComponentProps } from "react";

const BLOCK_MIN_WIDTH = 20;
const BLOCK_MIN_HEIGHT = 20;

const ResizerContext = createContext<{
  onMouseDown: (e: React.MouseEvent, index: number) => void;
  enabled: boolean;
}>({
  onMouseDown: () => {},
  enabled: true,
});

export function BlockResizerRoot({
  minWidth = BLOCK_MIN_WIDTH,
  minHeight = BLOCK_MIN_HEIGHT,
  enabled = true,
  maintainAspectRatio = false,
  onBlockResize,
  onBlockResizeStart,
  onBlockResizeEnd,
  className,
  children,
  ...rest
}: {
  minWidth?: number;
  minHeight?: number;
  enabled?: boolean;
  maintainAspectRatio?: boolean;
  onBlockResize?: (size: { width: number; height: number }) => void;
  onBlockResizeStart?: () => void;
  onBlockResizeEnd?: () => void;
} & ComponentProps<"div">) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onBlockResizeRef = useRef(onBlockResize);
  const onBlockResizeStartRef = useRef(onBlockResizeStart);
  const onBlockResizeEndRef = useRef(onBlockResizeEnd);

  onBlockResizeRef.current = onBlockResize;
  onBlockResizeStartRef.current = onBlockResizeStart;
  onBlockResizeEndRef.current = onBlockResizeEnd;

  const handleDotMouseDown = useCallback(
    (e: React.MouseEvent, index: number) => {
      if (!containerRef.current) {
        return;
      }
      e.preventDefault();
      let isResizing = true;
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = containerRef.current.offsetWidth;
      const startHeight = containerRef.current.offsetHeight;

      onBlockResizeStartRef.current?.();

      const onMouseMove = (e2: MouseEvent) => {
        if (!isResizing) {
          return;
        }

        let newWidth: number;
        let newHeight: number;
        if (maintainAspectRatio && startWidth > 0) {
          const aspectRatio = startHeight / startWidth;
          const signX = index % 2 === 0 ? -1 : 1;
          const signY = index < 2 ? -1 : 1;
          const diagonalDelta =
            (signX * (e2.clientX - startX) + signY * (e2.clientY - startY)) / Math.SQRT2;
          newWidth = Math.max(Math.round(startWidth + diagonalDelta), minWidth);
          newHeight = Math.max(Math.round(newWidth * aspectRatio), minHeight);
        } else {
          const deltaX = index % 2 === 0 ? -(e2.clientX - startX) : e2.clientX - startX;
          const deltaY = index < 2 ? -(e2.clientY - startY) : e2.clientY - startY;
          newWidth = Math.max(startWidth + deltaX, minWidth);
          newHeight = Math.max(startHeight + deltaY, minHeight);
        }
        onBlockResizeRef.current?.({ width: newWidth, height: newHeight });
      };

      const onMouseUp = () => {
        if (isResizing) {
          isResizing = false;
          onBlockResizeEndRef.current?.();
        }

        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [minHeight, minWidth, maintainAspectRatio],
  );

  const providerValue = useMemo(
    () => ({ onMouseDown: handleDotMouseDown, enabled }),
    [handleDotMouseDown, enabled],
  );

  return (
    <ResizerContext.Provider value={providerValue}>
      <div
        ref={containerRef}
        style={{ minWidth, minHeight }}
        className={twMerge(
          "relative border border-transparent",
          enabled && "border-blue-500",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    </ResizerContext.Provider>
  );
}

export function BlockResizerControls({
  getLabel,
}: {
  getLabel: (dotIndex: 1 | 2 | 3 | 4) => string;
}) {
  const { onMouseDown, enabled } = useContext(ResizerContext);

  if (!enabled) return null;

  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => {
        return (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            type="button"
            aria-label={getLabel((index + 1) as 1 | 2 | 3 | 4)}
            onMouseDown={(e) => onMouseDown(e, index)}
            className={twMerge(
              "absolute z-10 w-2.5 h-2.5 bg-document-bg border border-blue-500 rounded-full",
              index === 0 && "-top-1 -left-1 cursor-nwse-resize",
              index === 1 && "-top-1 -right-1 cursor-nesw-resize",
              index === 2 && "-bottom-1 -left-1 cursor-nesw-resize",
              index === 3 && "-bottom-1 -right-1 cursor-nwse-resize",
            )}
          />
        );
      })}
    </>
  );
}
